import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { FloatingActionIslandContent } from '@/components/ui/FloatingActionIslandContent'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { evalApiClient } from '@/lib/api/eval.client'
import type { EvalRun, EvalResult } from '@super/shared-types'

interface Props {
  run: EvalRun
}

export function EvalRunDetailView({ run }: Props) {
  const navigate = useNavigate()
  const [results, setResults] = useState<EvalResult[]>([])
  const [total, setTotal] = useState(0)
  const [isFetching, setIsFetching] = useState(false)
  const [failuresOnly, setFailuresOnly] = useState(false)
  const [offset, setOffset] = useState(0)
  const limit = 50

  useEffect(() => {
    let cancelled = false
    setIsFetching(true)

    evalApiClient
      .listResults({
        runId: run.id,
        passed: failuresOnly ? false : undefined,
        limit,
        offset,
      })
      .then(response => {
        if (cancelled) return
        if (response) {
          setResults(response.data)
          setTotal(response.pagination.total)
        }
      })
      .finally(() => {
        if (!cancelled) setIsFetching(false)
      })

    return () => {
      cancelled = true
    }
  }, [run.id, failuresOnly, offset])

  const passRate = run.totalCases
    ? `${Math.round((run.passedCases / run.totalCases) * 100)}%`
    : '—'

  const currentPage = Math.floor(offset / limit) + 1
  const totalPages = Math.ceil(total / limit)

  return (
    <div className="flex flex-col gap-4">
      <FloatingActionIslandContent
        show={totalPages > 1}
        componentId="eval-results-pagination"
        priority={10}
      >
        <div className="inline-flex w-full lg:w-[520px] max-w-2xl">
          <div className="flex-grow items-center space-x-2 text-sm text-muted-foreground whitespace-nowrap pointer-events-none hidden md:flex">
            <span className="font-medium">
              {offset + 1}–{Math.min(offset + limit, total)} of {total} results
            </span>
          </div>
          <div className="flex items-center space-x-2 w-[240px] justify-center md:justify-end pointer-events-auto">
            <div className="inline-flex items-center space-x-1">
              <button
                onClick={() => setOffset(offset - limit)}
                disabled={currentPage <= 1}
                className="pointer-events-auto relative inline-flex items-center justify-center w-10 h-10 text-sm font-medium text-muted-foreground bg-background/80 border border-border rounded-lg hover:bg-muted hover:border-border disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <div className="inline-flex items-center space-x-1 justify-end">
                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                  let pageNumber
                  if (totalPages <= 5) {
                    pageNumber = i + 1
                  } else if (currentPage <= 3) {
                    pageNumber = i + 1
                  } else if (currentPage >= totalPages - 2) {
                    pageNumber = totalPages - 4 + i
                  } else {
                    pageNumber = currentPage - 2 + i
                  }
                  return (
                    <button
                      key={pageNumber}
                      onClick={() => setOffset((pageNumber - 1) * limit)}
                      className={cn(
                        'pointer-events-auto relative inline-flex items-center justify-center w-10 h-10 text-sm font-medium border rounded-lg transition-all',
                        pageNumber === currentPage
                          ? 'z-10 bg-black/90 text-white border-black'
                          : 'bg-background/80 border-border text-muted-foreground hover:bg-muted hover:border-border',
                      )}
                    >
                      {pageNumber}
                    </button>
                  )
                })}
              </div>
              <button
                onClick={() => setOffset(offset + limit)}
                disabled={currentPage >= totalPages}
                className="pointer-events-auto relative inline-flex items-center justify-center w-10 h-10 text-sm font-medium text-muted-foreground bg-background/80 border border-border rounded-lg hover:bg-muted hover:border-border disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </FloatingActionIslandContent>

      {/* Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          {
            label: 'Scope',
            value: run.scope === 'COMPANY' ? run.companyId : 'Global',
          },
          { label: 'Pass rate', value: passRate },
          {
            label: 'Actual cost',
            value:
              run.actualCostUsd != null
                ? `$${run.actualCostUsd.toFixed(4)}`
                : '—',
          },
          { label: 'Status', value: run.status },
        ].map(({ label, value }) => (
          <div key={label} className="rounded-md border p-3">
            <p className="text-xs text-muted-foreground">{label}</p>
            <p className="font-medium text-sm mt-0.5 truncate">{value}</p>
          </div>
        ))}
      </div>

      {/* Filter toggle */}
      <div className="flex gap-2">
        <Button
          variant={failuresOnly ? 'outline' : 'default'}
          size="sm"
          onClick={() => {
            setFailuresOnly(false)
            setOffset(0)
          }}
        >
          All
        </Button>
        <Button
          variant={failuresOnly ? 'default' : 'outline'}
          size="sm"
          onClick={() => {
            setFailuresOnly(true)
            setOffset(0)
          }}
        >
          Failures Only
        </Button>
      </div>

      {/* Results table */}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Case</TableHead>
              <TableHead>Expected</TableHead>
              <TableHead>Actual</TableHead>
              <TableHead>Confidence</TableHead>
              <TableHead>Result</TableHead>
              <TableHead>Duration</TableHead>
              <TableHead>Cost</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isFetching ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell>
                    <Skeleton className="h-4 w-36" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-24" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-24" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-12" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-5 w-12" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-16" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-16" />
                  </TableCell>
                </TableRow>
              ))
            ) : results.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={7}
                  className="text-center text-muted-foreground py-8"
                >
                  No results.
                </TableCell>
              </TableRow>
            ) : (
              results.map(r => (
                <TableRow
                  key={r.id}
                  className={`cursor-pointer hover:bg-muted/50 ${r.passed ? '' : 'bg-error-subtle/30'}`}
                  onClick={() =>
                    navigate(`/evaluations/runs/${r.runId}/results/${r.id}`)
                  }
                >
                  <TableCell className="font-medium text-sm">
                    {r.case.name}
                  </TableCell>
                  <TableCell className="font-mono text-xs max-w-[120px] truncate">
                    {JSON.stringify(r.expectedOutput)}
                  </TableCell>
                  <TableCell className="font-mono text-xs max-w-[120px] truncate">
                    {r.actualOutput ? JSON.stringify(r.actualOutput) : '—'}
                  </TableCell>
                  <TableCell>
                    {r.confidence != null
                      ? `${(r.confidence * 100).toFixed(0)}%`
                      : '—'}
                  </TableCell>
                  <TableCell>
                    <Badge
                      className={
                        r.passed
                          ? 'bg-success-muted text-success'
                          : 'bg-error-muted text-error'
                      }
                    >
                      {r.passed ? 'Pass' : 'Fail'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm">
                    {r.durationMs != null ? `${r.durationMs}ms` : '—'}
                  </TableCell>
                  <TableCell className="font-mono text-xs">
                    {r.costUsd != null ? `$${r.costUsd.toFixed(5)}` : '—'}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}
